import type { ChatCommandMenuItem } from "./ChatCommandMenu";
import type { CommandMenuView } from "./ChatCommandMenu.sections";

import React, { useEffect } from "react";

import { logger } from "@/telemetry";

interface CommandMenuKeyboardOptions {
  visible: boolean;
  view: CommandMenuView;
  /** The flat list of ACTION items (toggle rows excluded) the arrows move through. */
  actionItems: ChatCommandMenuItem[];
  selectedIndex: number;
  setSelectedIndex: React.Dispatch<React.SetStateAction<number>>;
  setView: React.Dispatch<React.SetStateAction<CommandMenuView>>;
  onClose: () => void;
}

/**
 * Document-level keyboard navigation for ChatCommandMenu while it is visible:
 * ArrowUp/Down move through the action items and wrap, Enter activates the
 * highlighted one, and Esc steps back from a drill-in sub-view before it closes
 * the menu.
 */
export const useCommandMenuKeyboard = ({
  visible,
  view,
  actionItems,
  selectedIndex,
  setSelectedIndex,
  setView,
  onClose,
}: CommandMenuKeyboardOptions) => {
  useEffect(() => {
    if (!visible) return;

    const moveDown = () =>
      setSelectedIndex((prev) => (actionItems.length === 0 ? 0 : (prev + 1) % actionItems.length));

    const moveUp = () =>
      setSelectedIndex((prev) =>
        actionItems.length === 0 ? 0 : (prev - 1 + actionItems.length) % actionItems.length,
      );

    const activate = () => {
      const active = actionItems[selectedIndex];

      if (active) {
        logger.info("ChatCommandMenu: item activated via keyboard", { id: active.id });
        active.onSelect?.();
        if (!active.keepOpenOnSelect) onClose();
      }
    };

    const escape = () => {
      // In a drill-in sub-view, Esc steps back to the main menu first.
      if (view === "workflows" || view === "skills") {
        setView("main");
      } else {
        onClose();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          moveDown();
          break;

        case "ArrowUp":
          e.preventDefault();
          moveUp();
          break;

        case "Enter":
          e.preventDefault();
          activate();
          break;

        case "Escape":
          e.preventDefault();
          escape();
          break;

        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [visible, view, actionItems, selectedIndex, onClose, setSelectedIndex, setView]);
};
